// Folder tree sidebar for the Media Center.
//
// - Renders the BE folder tree (`GET /admin/folders/tree`) as an AntD Tree.
// - The "All files" pseudo-node (id="__root__") clears the folder filter.
// - Drag-drop moves folders via `POST /admin/folders/{id}/move`.
// - Right-click / "+" buttons expose create/rename/delete affordances.

import { useMemo, useState } from 'react';
import { App, Button, Dropdown, Empty, Input, Modal, Space, Tooltip, Tree, Typography } from 'antd';
import type { TreeProps } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Folder,
  FolderClosed,
  FolderOpen,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { foldersApi, type FolderTreeNode } from '@api/folders';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useConfirm } from '@shared/components/ConfirmDialog';

const { Text } = Typography;

const ROOT_KEY = '__root__';

export interface FolderTreeSidebarProps {
  /** Currently selected folder id (or null for "all files"). */
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
}

interface ContextAction {
  type: 'create' | 'rename' | 'delete';
  folder?: { id: string; name: string; rowVersion: string | null };
}

/**
 * Flatten the tree into a lookup table keyed by folder id so the rename
 * handler can grab the rowVersion (ISSUE-62) without a follow-up GET.
 */
function indexByIdAndBuildData(nodes: FolderTreeNode[]): {
  data: DataNode[];
  byId: Map<string, FolderTreeNode>;
  allKeys: string[];
} {
  const byId = new Map<string, FolderTreeNode>();
  const allKeys: string[] = [];
  const walk = (list: FolderTreeNode[]): DataNode[] =>
    list.map((n) => {
      byId.set(n.id, n);
      if (n.children.length) allKeys.push(n.id);
      const hasChildren = n.children.length > 0;
      return {
        key: n.id,
        title: n.name,
        icon: hasChildren ? <Folder size={14} /> : <FolderClosed size={14} />,
        children: hasChildren ? walk(n.children) : undefined,
      };
    });
  return { data: walk(nodes), byId, allKeys };
}

// Yükleme sırasında stabil boş ağaç — render-adjust deseninin kimlik
// karşılaştırması her render'da yeni [] üretilirse sonsuz döngüye girer.
const EMPTY_TREE: FolderTreeNode[] = [];

export function FolderTreeSidebar({ selectedFolderId, onSelect }: FolderTreeSidebarProps) {
  const { t } = useTranslation('media');
  const qc = useQueryClient();
  const { message } = App.useApp();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [contextAction, setContextAction] = useState<ContextAction | null>(null);
  const [inputValue, setInputValue] = useState('');

  const { data: treeData0, isLoading } = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    staleTime: 60_000,
  });
  const tree = treeData0 ?? EMPTY_TREE;

  // ISSUE-62 — Build the AntD tree data and an id→node lookup in one pass so
  // the rename mutation can read rowVersion directly from the tree response.
  const { treeData, nodesById, allParentKeys } = useMemo(() => {
    const { data, byId, allKeys } = indexByIdAndBuildData(tree);
    const wrapped: DataNode[] = [
      {
        key: ROOT_KEY,
        title: t('folderTree.allFiles'),
        icon: <FolderOpen size={14} />,
        children: data.length ? data : undefined,
      },
    ];
    return { treeData: wrapped, nodesById: byId, allParentKeys: allKeys };
  }, [tree, t]);

  const filteredKeys = useMemo(() => {
    if (!search.trim()) return null;
    const needle = search.toLowerCase();
    const out: React.Key[] = [];
    const walk = (nodes: FolderTreeNode[]) => {
      for (const n of nodes) {
        if (n.name.toLowerCase().includes(needle)) out.push(n.id);
        walk(n.children);
      }
    };
    walk(tree);
    return out;
  }, [search, tree]);

  // Controlled expanded state so we can offer expand-all / collapse-all.
  // Seed it with the root + active route + search-match parents.
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([ROOT_KEY]);
  // Effect'siz "adjust state on change" deseni (set-state-in-effect'ten
  // kaçınır): arama eşleşmeleri değişince eşleşen düğümleri render sırasında aç.
  const [prevFilteredKeys, setPrevFilteredKeys] = useState<React.Key[] | null>(filteredKeys);
  if (filteredKeys !== prevFilteredKeys) {
    setPrevFilteredKeys(filteredKeys);
    if (filteredKeys && filteredKeys.length > 0) {
      setExpandedKeys((prev) =>
        Array.from(new Set<React.Key>([...prev, ROOT_KEY, ...filteredKeys])),
      );
    }
  }
  // When the active folder changes externally (or the tree finishes loading),
  // make sure its ancestor chain is expanded so the selection is visible.
  const [prevExpandSig, setPrevExpandSig] = useState<{
    sel: string | null | undefined;
    nodes: Map<string, FolderTreeNode>;
  } | null>(null);
  if (!prevExpandSig || prevExpandSig.sel !== selectedFolderId || prevExpandSig.nodes !== nodesById) {
    setPrevExpandSig({ sel: selectedFolderId, nodes: nodesById });
    if (selectedFolderId) {
      const ancestors: React.Key[] = [];
      let cur = nodesById.get(selectedFolderId);
      while (cur?.parentFolderId) {
        ancestors.push(cur.parentFolderId);
        cur = nodesById.get(cur.parentFolderId);
      }
      if (ancestors.length > 0) {
        setExpandedKeys((prev) =>
          Array.from(new Set<React.Key>([...prev, ROOT_KEY, ...ancestors])),
        );
      }
    }
  }

  const expandAll = () =>
    setExpandedKeys([ROOT_KEY, ...allParentKeys] as React.Key[]);
  const collapseAll = () => setExpandedKeys([ROOT_KEY]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['folders'] });
    void qc.invalidateQueries({ queryKey: ['files'] });
  };

  const createMut = useMutation({
    mutationFn: (vars: { name: string; parentId: string | null }) =>
      foldersApi.create(vars.name, vars.parentId),
    onSuccess: () => {
      void message.success(t('folderTree.toast.created'));
      invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const renameMut = useMutation({
    mutationFn: async (vars: { id: string; name: string; rowVersion: string | null }) => {
      // ISSUE-62 — `rowVersion` now travels in the tree response, so the
      // earlier extra `GET /folders/{id}` round-trip is gone. We only fall
      // back to a fresh fetch if the tree node somehow lacks the token
      // (e.g. a stale cache after an older BE deploy).
      let rowVersion = vars.rowVersion;
      if (!rowVersion) {
        const detail = await foldersApi.get(vars.id);
        rowVersion = detail.rowVersion;
      }
      await foldersApi.rename(vars.id, vars.name, rowVersion);
    },
    onSuccess: () => {
      void message.success(t('folderTree.toast.renamed'));
      invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => foldersApi.remove(id),
    onSuccess: () => {
      void message.success(t('folderTree.toast.deleted'));
      if (contextAction?.folder?.id === selectedFolderId) onSelect(null);
      invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const moveMut = useMutation({
    mutationFn: (vars: { id: string; newParentId: string | null }) =>
      foldersApi.move(vars.id, vars.newParentId),
    onSuccess: () => {
      void message.success(t('folderTree.toast.moved'));
      invalidate();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const handleDrop: TreeProps['onDrop'] = (info) => {
    const dragKey = String(info.dragNode.key);
    const dropKey = String(info.node.key);
    if (dragKey === ROOT_KEY) return;
    const newParentId = dropKey === ROOT_KEY ? null : dropKey;
    moveMut.mutate({ id: dragKey, newParentId });
  };

  const handleSubmit = () => {
    if (!contextAction) return;
    const value = inputValue.trim();
    if (contextAction.type === 'create' && value) {
      createMut.mutate({ name: value, parentId: contextAction.folder?.id ?? null });
    } else if (contextAction.type === 'rename' && value && contextAction.folder) {
      renameMut.mutate({
        id: contextAction.folder.id,
        name: value,
        rowVersion: contextAction.folder.rowVersion,
      });
    }
    setContextAction(null);
    setInputValue('');
  };

  const renderTitle = (node: DataNode) => {
    const key = String(node.key);
    if (key === ROOT_KEY) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Text strong>{t('folderTree.allFiles')}</Text>
        </span>
      );
    }
    return (
      <span className="ds-folder-row">
        <span className="ds-folder-row__name">{nodesById.get(key)?.name ?? ''}</span>
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'create',
                icon: <FolderPlus size={12} />,
                label: t('folderTree.menu.createSubfolder'),
              },
              { key: 'rename', icon: <Pencil size={12} />, label: t('folderTree.menu.rename') },
              {
                key: 'delete',
                icon: <Trash2 size={12} />,
                label: t('folderTree.menu.delete'),
                danger: true,
              },
            ],
            onClick: ({ key: actionKey, domEvent }) => {
              domEvent.stopPropagation();
              // ISSUE-62 — pull rowVersion + canonical name straight from the
              // tree response (no follow-up GET /folders/{id} round-trip).
              const node2 = nodesById.get(key);
              const folderName = node2?.name ?? '';
              if (actionKey === 'create') {
                setContextAction({
                  type: 'create',
                  folder: {
                    id: key,
                    name: folderName,
                    rowVersion: node2?.rowVersion ?? null,
                  },
                });
                setInputValue('');
              } else if (actionKey === 'rename') {
                setContextAction({
                  type: 'rename',
                  folder: {
                    id: key,
                    name: folderName,
                    rowVersion: node2?.rowVersion ?? null,
                  },
                });
                setInputValue(folderName);
              } else if (actionKey === 'delete') {
                void confirm({
                  title: t('folderTree.deleteConfirm.title'),
                  description: t('folderTree.deleteConfirm.description', { name: folderName }),
                  okText: t('folderTree.deleteConfirm.okText'),
                  cancelText: t('folderTree.deleteConfirm.cancelText'),
                  danger: true,
                }).then((ok) => {
                  if (ok) deleteMut.mutate(key);
                });
              }
            },
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreHorizontal size={12} />}
            onClick={(e) => e.stopPropagation()}
            aria-label={t('folderTree.actionsLabel')}
            className="ds-folder-row__action"
          />
        </Dropdown>
      </span>
    );
  };

  const allExpanded =
    allParentKeys.length > 0 &&
    allParentKeys.every((k) => expandedKeys.includes(k as React.Key));

  return (
    <div
      data-testid="folder-tree-sidebar"
      className="ds-folder-tree"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <style>{`
        /* Make sure title slot stretches so the action button has somewhere
           to sit on the right; AntD's default is shrink-to-fit which collapses
           our flex row to two lines when the name is long. */
        .ds-folder-tree .ant-tree-title {
          display: inline-block;
          width: calc(100% - 28px);
          vertical-align: middle;
        }
        .ds-folder-row {
          display: flex;
          align-items: center;
          gap: 4px;
          width: 100%;
          min-width: 0;
        }
        .ds-folder-row__name {
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ds-folder-row__action {
          flex: 0 0 auto;
          opacity: 0;
          transition: opacity 120ms ease-in-out;
        }
        .ds-folder-tree .ant-tree-treenode:hover .ds-folder-row__action,
        .ds-folder-tree .ant-tree-treenode-selected .ds-folder-row__action,
        .ds-folder-row__action:focus-visible {
          opacity: 1;
        }
        .ds-folder-tree .ant-tree-treenode-selected > .ant-tree-node-content-wrapper {
          background: var(--ds-bg-subtle, rgba(28, 105, 212, 0.08));
          font-weight: 600;
        }
        .ds-folder-tree .ant-tree-treenode {
          padding: 1px 0;
        }
        .ds-folder-tree .ant-tree-iconEle {
          vertical-align: middle;
        }
        .ds-folder-tree .ant-tree-indent-unit::before {
          border-inline-end: 1px dashed var(--ds-border, rgba(0, 0, 0, 0.08));
        }
      `}</style>

      {/* Compact sticky toolbar — search + expand/collapse + create-root in one row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          paddingBottom: 4,
          borderBottom: '1px solid var(--ds-border, rgba(0,0,0,0.06))',
        }}
      >
        <Text
          strong
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            color: 'var(--ds-text-tertiary)',
            letterSpacing: 0.4,
            whiteSpace: 'nowrap',
          }}
        >
          {t('folderTree.label')}
        </Text>
        <Input
          size="small"
          placeholder={t('folderTree.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1, minWidth: 0 }}
        />
        <Tooltip title={allExpanded ? t('folderTree.collapseAll') : t('folderTree.expandAll')}>
          <Button
            type="text"
            size="small"
            icon={allExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
            onClick={() => (allExpanded ? collapseAll() : expandAll())}
            disabled={allParentKeys.length === 0}
            aria-label={allExpanded ? t('folderTree.collapseAll') : t('folderTree.expandAll')}
          />
        </Tooltip>
        <Tooltip title={t('folderTree.newRootFolder')}>
          <Button
            type="text"
            size="small"
            icon={<FolderPlus size={14} />}
            onClick={() => {
              setContextAction({ type: 'create' });
              setInputValue('');
            }}
            aria-label={t('folderTree.newRootFolder')}
            data-testid="folder-create-root"
          />
        </Tooltip>
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!isLoading && tree.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 12 }}>
                {t('folderTree.empty')}
                <br />
                {t('folderTree.emptyHintPrefix')} <FolderPlus size={12} /> {t('folderTree.emptyHint')}
              </span>
            }
            style={{ marginTop: 24 }}
          />
        ) : (
          <Tree
            showIcon
            blockNode
            draggable={{ icon: false, nodeDraggable: (n) => String(n.key) !== ROOT_KEY }}
            onDrop={handleDrop}
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            autoExpandParent={!!filteredKeys}
            selectedKeys={selectedFolderId ? [selectedFolderId] : [ROOT_KEY]}
            onSelect={(keys) => {
              const k = keys[0];
              if (!k || k === ROOT_KEY) onSelect(null);
              else onSelect(String(k));
            }}
            titleRender={renderTitle}
          />
        )}
      </div>
      <Modal
        open={contextAction !== null}
        title={
          contextAction?.type === 'create'
            ? contextAction.folder
              ? t('folderTree.modal.subfolderTitle', { name: contextAction.folder.name })
              : t('folderTree.modal.newRootTitle')
            : t('folderTree.modal.renameTitle')
        }
        onCancel={() => {
          setContextAction(null);
          setInputValue('');
        }}
        onOk={handleSubmit}
        okText={t('folderTree.modal.okText')}
        cancelText={t('folderTree.modal.cancelText')}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>{t('folderTree.modal.nameLabel')}</Text>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('folderTree.modal.namePlaceholder')}
            data-testid="folder-name-input"
            onPressEnter={handleSubmit}
          />
        </Space>
      </Modal>
    </div>
  );
}
